/**
 * Empty State Presets
 *
 * Pre-configured empty states for common scenarios across the platform.
 * Each preset provides contextual CTAs to guide users to the next action.
 */

import {
  FileText,
  Upload,
  Building2,
  Users,
  Receipt,
  FolderOpen,
  Search,
  Filter,
  AlertCircle,
  Inbox,
  FileSpreadsheet,
  Calculator,
  Sparkles,
} from "lucide-react";
import { EmptyState } from "./empty-state";

interface PresetEmptyStateProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  onPrimaryAction?: () => void;
}

/**
 * No transactions found - first time user
 */
export function EmptyTransactions({
  className,
  size,
  onPrimaryAction,
}: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={Receipt}
      title="No Transactions Yet"
      description="Start by importing your bank statements or manually adding your first transaction."
      className={className}
      size={size}
      actions={[
        {
          label: "Import Bank Statement",
          href: "/bank-import",
          icon: Upload,
        },
        {
          label: "Add Transaction",
          onClick: onPrimaryAction,
          variant: "outline",
          icon: FileText,
        },
      ]}
    />
  );
}

/**
 * No transactions match current filters
 */
export function EmptyFilteredTransactions({
  className,
  size,
  onClearFilters,
}: PresetEmptyStateProps & { onClearFilters?: () => void }) {
  return (
    <EmptyState
      icon={Filter}
      title="No Matching Transactions"
      description="No transactions match your current filters. Try adjusting your search criteria or clear all filters."
      className={className}
      size={size}
      actions={[
        {
          label: "Clear Filters",
          onClick: onClearFilters,
          variant: "outline",
          icon: Filter,
        },
      ]}
    />
  );
}

/**
 * No documents found
 */
export function EmptyDocuments({ className, size }: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={FolderOpen}
      title="No Documents Yet"
      description="Upload invoices, receipts, and other financial documents for AI-powered analysis and categorization."
      className={className}
      size={size}
      actions={[
        {
          label: "Upload Documents",
          href: "/file-inbox",
          icon: Upload,
        },
        {
          label: "Learn More",
          href: "/intelligence",
          variant: "outline",
          icon: Sparkles,
        },
      ]}
    />
  );
}

/**
 * No companies found
 */
export function EmptyCompanies({
  className,
  size,
  onPrimaryAction,
}: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={Building2}
      title="No Companies Yet"
      description="Create your first company profile to start tracking financial data and generating tax reports."
      className={className}
      size={size}
      actions={[
        {
          label: "Add Company",
          onClick: onPrimaryAction,
          icon: Building2,
        },
        {
          label: "Document Discovery",
          href: "/company-discovery",
          variant: "outline",
          icon: Sparkles,
        },
      ]}
    />
  );
}

/**
 * No vendors found
 */
export function EmptyVendors({
  className,
  size,
  onPrimaryAction,
}: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={Users}
      title="No Vendors Yet"
      description="Add your suppliers and service providers to track expenses and simplify categorization."
      className={className}
      size={size}
      actions={[
        {
          label: "Add Vendor",
          onClick: onPrimaryAction,
          icon: Users,
        },
      ]}
    />
  );
}

/**
 * No customers found
 */
export function EmptyCustomers({
  className,
  size,
  onPrimaryAction,
}: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={Users}
      title="No Customers Yet"
      description="Add your clients to track income and generate customer-specific reports."
      className={className}
      size={size}
      actions={[
        {
          label: "Add Customer",
          onClick: onPrimaryAction,
          icon: Users,
        },
      ]}
    />
  );
}

/**
 * No search results
 */
export function EmptySearchResults({
  className,
  size,
  query,
}: PresetEmptyStateProps & { query?: string }) {
  return (
    <EmptyState
      icon={Search}
      title="No Results Found"
      description={
        query
          ? `No results found for "${query}". Try a different search term.`
          : "No results match your search criteria."
      }
      className={className}
      size={size}
    />
  );
}

/**
 * No file inbox items
 */
export function EmptyFileInbox({ className, size }: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={Inbox}
      title="Inbox Empty"
      description="Drop files here or click to upload. We'll automatically extract and categorize your financial data."
      className={className}
      size={size}
      actions={[
        {
          label: "Upload Files",
          href: "#upload",
          icon: Upload,
        },
      ]}
    />
  );
}

/**
 * No categorization rules
 */
export function EmptyCategorizationRules({
  className,
  size,
  onPrimaryAction,
}: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={Sparkles}
      title="No Rules Created"
      description="Create rules to automatically categorize transactions based on patterns in your data."
      className={className}
      size={size}
      actions={[
        {
          label: "Create Rule",
          onClick: onPrimaryAction,
          icon: Sparkles,
        },
        {
          label: "View Suggestions",
          href: "/rule-suggestions",
          variant: "outline",
        },
      ]}
    />
  );
}

/**
 * No templates found
 */
export function EmptyTemplates({
  className,
  size,
  onPrimaryAction,
}: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={FileSpreadsheet}
      title="No Templates Yet"
      description="Create templates for recurring transactions to save time on data entry."
      className={className}
      size={size}
      actions={[
        {
          label: "Create Template",
          onClick: onPrimaryAction,
          icon: FileSpreadsheet,
        },
      ]}
    />
  );
}

/**
 * No VAT data for period
 */
export function EmptyVATData({ className, size }: PresetEmptyStateProps) {
  return (
    <EmptyState
      icon={Calculator}
      title="No VAT Data Available"
      description="No transactions with VAT found for the selected period. Import transactions or adjust the date range."
      className={className}
      size={size}
      actions={[
        {
          label: "Import Transactions",
          href: "/bank-import",
          icon: Upload,
        },
      ]}
    />
  );
}

/**
 * Error state
 */
export function ErrorState({
  className,
  size,
  onRetry,
  message,
}: PresetEmptyStateProps & { onRetry?: () => void; message?: string }) {
  return (
    <EmptyState
      icon={AlertCircle}
      title="Something Went Wrong"
      description={
        message || "An error occurred while loading data. Please try again."
      }
      className={className}
      size={size}
      actions={
        onRetry
          ? [
              {
                label: "Try Again",
                onClick: onRetry,
              },
            ]
          : []
      }
    />
  );
}

export default {
  EmptyTransactions,
  EmptyFilteredTransactions,
  EmptyDocuments,
  EmptyCompanies,
  EmptyVendors,
  EmptyCustomers,
  EmptySearchResults,
  EmptyFileInbox,
  EmptyCategorizationRules,
  EmptyTemplates,
  EmptyVATData,
  ErrorState,
};
